import { useEffect } from 'react';
import { resolveFeatureFlag, useFeatureFlags, type FeatureFlagsResponse } from '~/hooks/queries/use-feature-flags';
import { seedUiRefactoring, UI_REFACTORING_FLAG, useUiRefactoringStore } from '~/lib/ui-refactoring';

/**
 * What the flags query has to say about `eddy.ui-refactoring` yet: `'wait'`
 * while the first answer is still out, otherwise how to seed. The FIRST failed
 * attempt already seeds (off, "the old UI"): the query's own retries back off
 * for several seconds, and every shell that calls this holds its loader until
 * the flag is seeded — main never waited on this request at all.
 */
export function uiRefactoringSeedInput(query: {
  data: FeatureFlagsResponse | undefined;
  isError: boolean;
  failureCount: number;
}): 'wait' | { data: FeatureFlagsResponse | undefined; failed: boolean } {
  if (query.data) return { data: query.data, failed: false };
  if (query.isError || query.failureCount > 0) return { data: undefined, failed: true };
  return 'wait';
}

/**
 * Whether the evaluated flags switch the refactored UI on. A stack with no flag
 * system (a local one) reads every flag on elsewhere, but nothing there ever SET
 * this one, so it reads off — the old UI is the default, and `?ui-refactoring=on`
 * (dev builds) is the way to ask for the new one.
 */
export function uiRefactoringFlagOn(input: { data: FeatureFlagsResponse | undefined; failed: boolean }): boolean {
  if (input.data?.configured === false) return false;
  return resolveFeatureFlag(UI_REFACTORING_FLAG, false, input.data, false, input.failed);
}

/**
 * FRONT-3223: seeds `eddy.ui-refactoring` once from `/api/feature-flags` and
 * says whether it is seeded. Every shell that renders refactored surfaces
 * calls it and holds its loader until it returns true — the app shell
 * (`layouts/app-layout.tsx`) and the pop-out window (`routes/popout.tsx`),
 * which sits outside it.
 */
export function useSeedUiRefactoring(): boolean {
  const flagsQuery = useFeatureFlags();
  const seeded = useUiRefactoringStore((s) => s.seeded);
  const input = uiRefactoringSeedInput(flagsQuery);
  useEffect(() => {
    if (seeded || input === 'wait') return;
    seedUiRefactoring(() => uiRefactoringFlagOn(input));
  }, [seeded, input]);
  return seeded;
}
